
import { Component } from 'vue-property-decorator';
import SheetHeader from '@/components/sheet/SheetHeader.vue';
import EventFlowStep from '@/model/enums/event-flow/event-flow-step';
import { SheetChatHistoryMessage, SheetWithItems } from '@/model/Sheet';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import EventFlow from '@/active-components/event-flow/EventFlow.vue';
import CustomModal from '@/components/modal/CustomModal.vue';
import InviteFormSheet from '@/components/sheet/InviteFormSheet.vue';
import { mixins } from 'vue-class-component';
import SheetInviteMixin from '@/mixins/SheetInviteMixin';
import ToastVariant from '@/model/enums/toast/toast-variant';
import { ApiError } from '@/model/ApiError';
import EventFlowGrid from '@/components/event-flow/EventFlowGrid.vue';
import '@/lib/font/Jost-Regular-normal';
import generateSheetPdf from '@/lib/pdf-util';
import { getEnumValueByEnumKey } from '@/lib/helper/event-flow-helper';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import { ExtraFunctionFormData } from '@/model/event-flow/EventFlow';
import EventFlowLocationName from '@/components/event-flow/EventFlowLocationName.vue';
import EventFlowLocationHeader from '@/components/event-flow/EventFlowLocationHeader.vue';
import AiChatbotButton from '@/components/sheet/AiChatbotButton.vue';
import AiChatbotWindow from '@/components/sheet/AiChatbotWindow.vue';
import FLAGS from '@/lib/flags';

@Component({
    components: {
        SheetHeader,
        EventFlow,
        CustomModal,
        InviteFormSheet,
        EventFlowGrid,
        FormCheckbox,
        EventFlowLocationName,
        EventFlowLocationHeader,
        AiChatbotButton,
        AiChatbotWindow,
    },
    computed: {
        ...mapGetters({
            getCurrentSheetWithItems: 'sheetStorage/getCurrentSheetWithItems',
            getSheetFlow: 'sheetStorage/getSheetFlow',
            getError: 'sheetStorage/getError',
        }),
    },
    methods: {
        ...mapMutations({
            setError: 'sheetStorage/setError',
        }),
        ...mapActions({
            fetchCurrentSheetWithItems:
                'sheetStorage/fetchCurrentSheetWithItems',
            downloadWordByToken: 'sheetStorage/downloadWordByToken',
        }),
    },
})
export default class Sheet extends mixins(SheetInviteMixin) {
    fetchCurrentSheetWithItems!: (
        token: string,
    ) => Promise<void | SheetWithItems>;

    downloadWordByToken!: (token: string) => Promise<void>;

    getCurrentSheetWithItems!: SheetWithItems | null;

    getError!: ApiError;

    setError!: (error: ApiError | null) => void;

    loading = true;

    exportLoading = false;

    isChatbotOpen = false;

    chatMessages: SheetChatHistoryMessage[] = [];

    exportLoadingMessageIndex = 0;

    exportLoadingIntervalId: number | null = null;

    readonly exportLoadingMessageKeys = [
        'sheet.export.loading.message-1',
        'sheet.export.loading.message-2',
        'sheet.export.loading.message-3',
    ];

    token = this.$route.params.token;

    EventFlowStep!: EventFlowStep | null;

    readonly isChatbotEnabled = FLAGS.chatbot;

    extraFunctionFormData: ExtraFunctionFormData = {
        showTime: true,
        showBrief: true,
        showLegend: true,
        showTimeline: true,
    };

    mounted(): void {
        if (!this.isChatbotEnabled) {
            this.isChatbotOpen = false;
        }

        this.fetchCurrentSheetWithItems(this.token)
            .then(() => {
                document.title = `${this.getCurrentSheetWithItems?.name} - Cascade`;
                this.closeChatbotIfUnavailable();
            })
            .catch(() => {
                document.title = `${this.$t(
                    'event.not-found',
                ).toString()} - Cascade`;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    downloadPdf(): void {
        if (this.exportLoading || !this.getCurrentSheetWithItems) {
            return;
        }

        this.startExportLoading();
        generateSheetPdf(this.getCurrentSheetWithItems)
            .catch((error: ApiError) => {
                this.showExportError(error);
            })
            .finally(() => {
                this.finishExportLoading();
            });
    }

    downloadWord(): void {
        if (this.exportLoading) {
            return;
        }

        this.startExportLoading();
        this.downloadWordByToken(this.token)
            .catch((error: ApiError) => {
                this.showExportError(error);
            })
            .finally(() => {
                this.finishExportLoading();
            });
    }

    get exportLoadingMessage(): string {
        return this.$t(
            this.exportLoadingMessageKeys[this.exportLoadingMessageIndex],
        ).toString();
    }

    get isChatbotAvailable(): boolean {
        return (
            this.isChatbotEnabled &&
            this.getCurrentSheetWithItems?.chatbotEnabled === true
        );
    }

    startExportLoading(): void {
        this.exportLoading = true;
        this.exportLoadingMessageIndex = 0;
        this.exportLoadingIntervalId = window.setInterval(() => {
            this.exportLoadingMessageIndex =
                (this.exportLoadingMessageIndex + 1) %
                this.exportLoadingMessageKeys.length;
        }, 3500);

        this.$bvModal.show('export-loading-modal');
    }

    finishExportLoading(): void {
        this.clearExportLoadingInterval();
        this.exportLoading = false;
        this.$bvModal.hide('export-loading-modal');
    }

    clearExportLoadingInterval(): void {
        if (this.exportLoadingIntervalId !== null) {
            window.clearInterval(this.exportLoadingIntervalId);
            this.exportLoadingIntervalId = null;
        }
    }

    showExportError(error: ApiError): void {
        this.showToast(
            this.$t(error?.key ?? 'general.internal.server.error').toString(),
            this.$t('sheet.notification.title').toString(),
            ToastVariant.DANGER,
        );
    }

    beforeDestroy(): void {
        this.clearExportLoadingInterval();
    }

    onSubmit(email: string): void {
        this.submitSheetInvite(
            email,
            this.getCurrentSheetWithItems?.id,
            'invite.success',
            'invite.title',
        );
    }

    get getStep(): number {
        return parseInt(
            getEnumValueByEnumKey(
                EventFlowStep,
                this.getCurrentSheetWithItems?.step,
            ) as string,
            10,
        );
    }

    close(): void {
        this.$bvModal.hide('invite-modal');
    }

    openInvite(): void {
        this.setError(null);
    }

    resetInvite(): void {
        this.setError(null);
    }
    onOpenChatbot(): void {
        if (!this.isChatbotAvailable) {
            return;
        }

        this.isChatbotOpen = true;
    }

    onCloseChatbot(): void {
        this.isChatbotOpen = false;
    }

    onChatbotMessagesChange(messages: SheetChatHistoryMessage[]): void {
        this.chatMessages = messages;
    }

    closeChatbotIfUnavailable(): void {
        if (this.isChatbotAvailable) {
            return;
        }

        this.isChatbotOpen = false;
        this.chatMessages = [];
    }
}
